import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';

const SERVICES = [
  ['Basic', [
    ['trim', 'Trim'], ['front_shoes', 'Front shoes'], ['hind_shoes', 'Hind shoes'], ['full_set', 'Full set / shoes all four']
  ]],
  ['New shoes', [
    ['new_front_shoes', 'New front shoes'], ['new_hind_shoes', 'New hind shoes'], ['new_full_set', 'New full set']
  ]],
  ['Resets', [
    ['reset_front', 'Reset front shoes'], ['reset_hind', 'Reset hind shoes'], ['reset_all', 'Reset all four shoes']
  ]],
  ['Pull shoes', [
    ['pull_front', 'Pull front shoes'], ['pull_hind', 'Pull hind shoes'], ['pull_all', 'Pull all shoes']
  ]],
  ['Specialty / corrective', [
    ['pads', 'Pads'], ['wedges', 'Wedges'], ['bar_shoes', 'Bar shoes'], ['heart_bar', 'Heart bar shoes'], ['egg_bar', 'Egg bar shoes'], ['glue_ons', 'Glue-on shoes'], ['therapeutic', 'Therapeutic shoeing'], ['corrective_trim', 'Corrective trim'], ['laminitis_support', 'Laminitis/founder support'], ['navicular_support', 'Navicular support package']
  ]],
  ['Problem visit', [
    ['lost_shoe', 'Lost shoe'], ['loose_shoe', 'Loose shoe'], ['abscess_check', 'Abscess check'], ['crack_repair', 'Crack repair'], ['hoof_soreness', 'Hoof soreness consult'], ['boot_fitting', 'Boot fitting'], ['vet_farrier_consult', 'Vet/farrier consult'], ['other', 'Other']
  ]]
];
const SERVICE_MAP = Object.fromEntries(SERVICES.flatMap(group => group[1]));
const emptyGroup = { service_type: 'trim', notes: '', horseIds: [], horseNotes: {} };
const ACTIVE_STATUSES = ['draft', 'sent', 'alternate_time_suggested', 'accepted', 'reschedule_requested'];

const formatDate = (d) => d ? new Date(`${d}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
const statusLabel = (s) => String(s || 'draft').replace(/_/g, ' ');
const displayTime = (time) => {
  if (!time) return '';
  const [hh, mm] = String(time).split(':');
  let h = Number(hh);
  if (Number.isNaN(h)) return time;
  const suffix = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return `${h}:${mm || '00'} ${suffix}`;
};
const appointmentTimeText = (a) => a?.exact_time ? displayTime(a.exact_time) : String(a?.time_preference || '').replace(/_/g, ' ');
const localZone = () => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Chicago'; }
  catch (e) { return 'America/Chicago'; }
};
const savedResponsePhone = () => {
  try { return window.localStorage.getItem('connected_rider_farrier_response_phone') || ''; }
  catch (e) { return ''; }
};
const farrierNotice = 'Farrier cancellation notice\n\nCanceling or requesting to reschedule within 48 hours of an appointment may not be considered acceptable notice by some farriers. Some farriers may charge a cancellation fee, trip fee, or require direct notice depending on their policy.\n\nPlease confirm you want to continue.';

const FarrierPage = () => {
  const navigate = useNavigate();
  const [horses, setHorses] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  const [acceptingId, setAcceptingId] = useState(null);
  const [actionId, setActionId] = useState(null);
  const [notesId, setNotesId] = useState(null);
  const [detailsId, setDetailsId] = useState(null);
  const [editApptId, setEditApptId] = useState(null);
  const [editDraft, setEditDraft] = useState(null);
  const openEdit = (a) => {
    setEditApptId(a.id);
    setEditDraft({
      proposed_date: a.proposed_date || '',
      exact_time: a.exact_time || '',
      groups: (a.service_groups || []).map(g => ({ service_type: g.service_type, service_label: g.service_label, notes: g.notes,
        horses: (g.horses || []).map(h => ({ horse_id: h.horse_id, horse_name: h.horse_name_snapshot, notes: h.horse_notes })) })),
    });
  };
  const saveAmend = async (a) => {
    if (!window.confirm('Save changes and resend to the provider for approval?')) return;
    setActionId(a.id);
    try {
      await api.post(`/farrier/appointments/${a.id}/amend`, {
        proposed_date: editDraft.proposed_date, exact_time: editDraft.exact_time,
        service_groups: editDraft.groups,
      });
      await api.post(`/farrier/appointments/${a.id}/send`);
      toast.success('Updated request sent for approval');
      window.location.reload();
    } catch (e) { toast.error((e.response && e.response.data && e.response.data.message) || 'Failed to save changes'); }
    finally { setActionId(null); }
  };
  const [notesDraft, setNotesDraft] = useState('');
  const saveOwnerNotes = async (a) => {
    try {
      await api.patch(`/farrier/appointments/${a.id}/notes`, { owner_notes: notesDraft });
      a.owner_notes = notesDraft;
      toast.success('Notes saved');
    } catch { toast.error('Failed to save notes'); }
  };
  const [rescheduleId, setRescheduleId] = useState(null);
  const [showAllHistory, setShowAllHistory] = useState(false);
  const [rescheduleForm, setRescheduleForm] = useState({ proposed_date: '', exact_time: '', message: '' });
  const [groupForm, setGroupForm] = useState(emptyGroup);
  const [serviceGroups, setServiceGroups] = useState([]);
  const [form, setForm] = useState({
    contact_id: '',
    farrier_name: '',
    farrier_phone: '',
    owner_notification_phone: savedResponsePhone(),
    meet_person: '',
    proposed_date: '',
    exact_time: '',
    duration_minutes: 120,
    time_zone: localZone(),
    location: '',
    facility_notes: '',
    message: ''
  });

  const farrierContacts = useMemo(() => contacts.filter(c => c.contact_type === 'farrier'), [contacts]);
  const activeAppointments = useMemo(() => appointments.filter(a => ACTIVE_STATUSES.includes(a.status || 'draft')), [appointments]);
  const historyAppointments = useMemo(() => appointments.filter(a => !ACTIVE_STATUSES.includes(a.status || 'draft')), [appointments]);
  const visibleHistory = showAllHistory ? historyAppointments : historyAppointments.slice(0, 5);

  const load = async () => {
    try {
      const [h, c, a] = await Promise.all([api.get('/horses'), api.get('/contacts'), api.get('/farrier/appointments')]);
      setHorses(h.data || []);
      setContacts(c.data || []);
      setAppointments(a.data || []);
    } catch (e) {
      toast.error('Failed to load farrier section');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const refreshContacts = async () => {
    try {
      const c = await api.get('/contacts');
      setContacts(c.data || []);
    } catch (e) {
      // non-blocking
    }
  };

  const updateAppointment = (updated) => setAppointments(prev => prev.map(a => a.id === updated.id ? updated : a));
  const setField = (key, value) => {
    if (key === 'owner_notification_phone') {
      try { window.localStorage.setItem('connected_rider_farrier_response_phone', value); } catch (e) {}
    }
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const pickContact = (id) => {
    const contact = farrierContacts.find(c => c.id === id);
    setForm(prev => ({
      ...prev,
      contact_id: id,
      farrier_name: contact ? contact.name : prev.farrier_name,
      farrier_phone: contact ? (contact.phone || '') : prev.farrier_phone
    }));
  };

  const toggleHorse = (id) => {
    setGroupForm(prev => {
      const has = prev.horseIds.includes(id);
      return { ...prev, horseIds: has ? prev.horseIds.filter(x => x !== id) : [...prev.horseIds, id] };
    });
  };

  const saveServiceGroup = () => {
    if (groupForm.horseIds.length === 0) return toast.error('Select at least one horse');
    const serviceLabel = SERVICE_MAP[groupForm.service_type] || groupForm.service_type;
    const group = {
      id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
      service_type: groupForm.service_type,
      service_label: serviceLabel,
      notes: groupForm.notes,
      horses: groupForm.horseIds.map(id => {
        const horse = horses.find(h => h.id === id);
        return { id, name: horse?.name || 'Horse', notes: groupForm.horseNotes[id] || '' };
      })
    };
    setServiceGroups(prev => [...prev, group]);
    setGroupForm(emptyGroup);
    toast.success(`${serviceLabel} added`);
  };

  const removeGroup = (id) => setServiceGroups(prev => prev.filter(g => g.id !== id));

  const createPayload = () => ({
    ...form,
    contact_id: form.contact_id || null,
    farrier_phone: form.farrier_phone || null,
    time_preference: 'exact',
    duration_minutes: Number(form.duration_minutes || 120),
    service_groups: serviceGroups.map(g => ({
      service_type: g.service_type,
      service_label: g.service_label,
      notes: g.notes,
      horses: g.horses.map(h => ({ id: h.id, notes: h.notes }))
    }))
  });

  const validateRequest = () => {
    if (!form.farrier_name.trim()) { toast.error('Farrier name required'); return false; }
    if (!form.proposed_date) { toast.error('Proposed date required'); return false; }
    if (!form.exact_time) { toast.error('Exact appointment time required'); return false; }
    if (!form.owner_notification_phone.trim()) { toast.error('Send farrier responses to phone is required'); return false; }
    if (serviceGroups.length === 0) { toast.error('Add at least one service group'); return false; }
    return true;
  };

  const saveDraft = async () => {
    if (!validateRequest()) return null;
    setSaving(true);
    try {
      const { data } = await api.post('/farrier/appointments', createPayload());
      setAppointments(prev => [data, ...prev]);
      await refreshContacts();
      toast.success('Farrier request saved');
      return data;
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to save farrier request');
      return null;
    } finally {
      setSaving(false);
    }
  };

  const textFarrier = async () => {
    const appt = await saveDraft();
    if (!appt) return;
    setSendingId(appt.id);
    try {
      const { data } = await api.post(`/farrier/appointments/${appt.id}/send`);
      updateAppointment(data);
      toast.success('Text sent to farrier');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Saved, but text failed');
    } finally {
      setSendingId(null);
    }
  };

  const acceptSuggestedTime = async (appt) => {
    setAcceptingId(appt.id);
    try {
      const { data } = await api.post(`/farrier/appointments/${appt.id}/accept-suggestion`);
      updateAppointment(data);
      toast.success('Suggested time accepted and saved to calendar');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to accept suggested time');
    } finally {
      setAcceptingId(null);
    }
  };

  const cancelAppointment = async (appt) => {
    const _ct = contacts.find(c => c.id === appt.contact_id);
    let _msg = farrierNotice;
    if (_ct && _ct.cancellation_notice_hours && appt.proposed_date) {
      const when = new Date(appt.proposed_date + 'T' + (appt.exact_time || '12:00'));
      const hrsLeft = (when.getTime() - Date.now()) / 3600000;
      if (hrsLeft > 0 && hrsLeft < _ct.cancellation_notice_hours) {
        _msg = (_ct.name || 'Your provider') + ' requests ' + _ct.cancellation_notice_hours + ' hours notice - this cancellation is inside that window. They will be notified now.\n\n' + _msg;
      }
    }
    if (!window.confirm(_msg)) return;
    setActionId(appt.id);
    try {
      const { data } = await api.post(`/farrier/appointments/${appt.id}/cancel`, {});
      updateAppointment(data);
      toast.success(appt.status === 'draft' ? 'Request canceled' : 'Cancellation saved and farrier notified if request was sent');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to cancel request');
    } finally {
      setActionId(null);
    }
  };

  const deleteAppointment = async (appt) => {
    if (!window.confirm('Delete this request and its communication history? Saved farrier contacts and completed horse-care history are not deleted.')) return;
    setActionId(appt.id);
    try {
      await api.delete(`/farrier/appointments/${appt.id}`);
      setAppointments(prev => prev.filter(a => a.id !== appt.id));
      toast.success('Request history deleted');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to delete request');
    } finally {
      setActionId(null);
    }
  };

  const openReschedule = (appt) => {
    setRescheduleId(appt.id);
    setRescheduleForm({ proposed_date: appt.proposed_date || '', exact_time: appt.exact_time || '', message: '' });
  };

  const submitReschedule = async (appt) => {
    if (!rescheduleForm.proposed_date || !rescheduleForm.exact_time) {
      toast.error('New date and exact time are required');
      return;
    }
    if (!window.confirm(farrierNotice)) return;
    setActionId(appt.id);
    try {
      const { data } = await api.post(`/farrier/appointments/${appt.id}/reschedule`, rescheduleForm);
      updateAppointment(data);
      setRescheduleId(null);
      toast.success('Reschedule request sent to farrier');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to request reschedule');
    } finally {
      setActionId(null);
    }
  };

  const appointmentCount = serviceGroups.reduce((sum, g) => sum + g.horses.length, 0);

  const renderAppointmentCard = (a) => (
    <div key={a.id} className="card" style={{ padding: '12px 14px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700 }}>{a.farrier_name}</div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{formatDate(a.proposed_date)} · {appointmentTimeText(a)}</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>{(a.service_groups || []).map(g => `${g.service_label} — ${(g.horses || []).map(h => h.horse_name_snapshot).join(', ') || (g.horses || []).length}`).join(' · ')}</div>
        </div>
        <span className={a.status === 'accepted' ? 'badge badge-green' : ['declined', 'canceled'].includes(a.status) ? 'badge badge-red' : 'badge badge-amber'}>{statusLabel(a.status)}</span>
      </div>

      {a.status === 'reschedule_requested' && (
        <div style={{ marginTop: 10, padding: 10, border: '1px solid var(--border-color)', borderRadius: 10, fontSize: 12, color: 'var(--text-secondary)' }}>
          Reschedule requested. The farrier still needs to accept, decline, or suggest another exact time.
        </div>
      )}

      {a.status === 'alternate_time_suggested' && a.suggested_date && a.suggested_exact_time && (
        <div style={{ marginTop: 10, padding: 10, border: '1px solid var(--border-color)', borderRadius: 10 }}>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Farrier suggested:</div>
          <div style={{ fontWeight: 700, fontSize: 14, marginTop: 2 }}>{formatDate(a.suggested_date)} at {displayTime(a.suggested_exact_time)}</div>
          {a.suggested_notes && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>{a.suggested_notes}</div>}
          <button className="btn btn-primary btn-full" style={{ marginTop: 8 }} onClick={() => acceptSuggestedTime(a)} disabled={acceptingId === a.id}>{acceptingId === a.id ? 'Accepting...' : 'Accept suggested time'}</button>
        </div>
      )}

      {rescheduleId === a.id && (
        <div style={{ marginTop: 10, padding: 10, border: '1px solid var(--border-color)', borderRadius: 10 }}>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>Request to reschedule</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>This keeps the farrier, horses, services, notes, and response phone. Only the requested date/time changes.</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div className="form-group">
              <label className="form-label">New date</label>
              {(() => { const ct = contacts.find(c => c.id === form.contact_id); return (ct && (ct.provider_instructions || ct.cancellation_notice_hours)) ? <div className="card" style={{ padding: '10px 14px', marginBottom: 8, fontSize: 12, color: 'var(--text-secondary)' }}><b>{ct.name || 'Provider'} asks:</b> {ct.provider_instructions || ''}{ct.cancellation_notice_hours ? ((ct.provider_instructions ? ' · ' : '') + ct.cancellation_notice_hours + ' hours cancellation notice') : ''}</div> : null; })()}
              <select className="form-input" style={{ marginBottom: 8 }} value="" onChange={e => { if (e.target.value) { setRescheduleForm(p => ({ ...p, proposed_date: e.target.value })); } }}><option value="">Quick pick a date…</option>{(() => { const opts = []; const push = (base, label) => [4, 6, 8].forEach(w => { const d = new Date(base); d.setDate(d.getDate() + w * 7); opts.push({ iso: d.toISOString().slice(0, 10), text: '+' + w + ' wks from ' + label + ' — ' + d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) }); }); push(new Date(), 'today'); const last = (appointments || []).map(a => a.proposed_date).filter(Boolean).sort().slice(-1)[0]; if (last) push(new Date(last + 'T00:00:00'), 'last appt (' + new Date(last + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ')'); return opts.map((o, k) => <option key={k} value={o.iso}>{o.text}</option>); })()}</select><input className="form-input" type="date" value={rescheduleForm.proposed_date} onChange={e => setRescheduleForm(p => ({ ...p, proposed_date: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">New exact time</label>
              <input className="form-input" type="time" value={rescheduleForm.exact_time} onChange={e => setRescheduleForm(p => ({ ...p, exact_time: e.target.value }))} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Optional note to farrier</label>
            <input className="form-input" value={rescheduleForm.message} onChange={e => setRescheduleForm(p => ({ ...p, message: e.target.value }))} placeholder="Reason or note, optional" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <button className="btn btn-secondary btn-sm" onClick={() => setRescheduleId(null)}>Cancel</button>
            <button className="btn btn-primary btn-sm" onClick={() => submitReschedule(a)} disabled={actionId === a.id}>{actionId === a.id ? 'Sending...' : 'Send request'}</button>
          </div>
        </div>
      )}

      {a.last_farrier_reply_body && (
        <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-secondary)' }}>
          Last text reply: {a.last_farrier_reply_body}
        </div>
      )}

      {a.status === 'accepted' && a.response_token && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 10 }}>
          <a className="btn btn-secondary btn-sm" href={`/api/farrier/respond/${a.response_token}/google-calendar`} target="_blank" rel="noreferrer">Google Calendar</a>
          <a className="btn btn-secondary btn-sm" href={`/api/farrier/appointments/${a.id}/calendar.ics`}>Apple / Outlook</a>
        </div>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
        {a.status === 'draft' && <button className="btn btn-danger btn-sm" onClick={() => deleteAppointment(a)} disabled={actionId === a.id}>Delete draft</button>}
        {a.status === 'sent' && <button className="btn btn-primary btn-sm" onClick={() => textFarrier(a)} disabled={actionId === a.id}>📱 Remind</button>}
        {['sent', 'alternate_time_suggested', 'accepted', 'reschedule_requested'].includes(a.status) && <button className="btn btn-secondary btn-sm" onClick={() => cancelAppointment(a)} disabled={actionId === a.id}>{a.status === 'accepted' ? 'Cancel appointment' : 'Cancel request'}</button>}
        {a.status === 'sent' && (() => { const cooling = a.last_reminded_at && (Date.now() - new Date(a.last_reminded_at).getTime()) < 86400000; return <button className="btn btn-primary btn-sm" disabled={cooling || actionId === a.id} onClick={async () => { try { await api.post(`/farrier/appointments/${a.id}/send`); toast.success('Reminder sent'); window.location.reload(); } catch (e) { toast.error((e.response && e.response.data && e.response.data.message) || 'Failed'); } }}>{cooling ? 'Reminded ✓' : '📱 Remind'}</button>; })()}
        {a.status === 'accepted' && <button className="btn btn-primary btn-sm" onClick={async () => { if (!window.confirm('Mark this appointment complete? It becomes locked service history.')) return; try { await api.post(`/farrier/appointments/${a.id}/complete`, { owner_notes_keep: true }); toast.success('Appointment completed'); window.location.reload(); } catch (e) { toast.error((e.response && e.response.data && e.response.data.message) || 'Failed to complete'); } }}>✔ Mark complete</button>}
        {!['completed', 'canceled'].includes(a.status) && <button className="btn btn-secondary btn-sm" onClick={() => (editApptId === a.id ? setEditApptId(null) : openEdit(a))}>✏ Edit & resend</button>}
        {editApptId === a.id && editDraft && (
          <div style={{ width: '100%', marginTop: 8, padding: 10, border: '1px solid var(--border-color)', borderRadius: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: 6 }}>Edit appointment (provider must re-approve)</div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <input type="date" className="form-input" value={editDraft.proposed_date} onChange={e => setEditDraft(d => ({ ...d, proposed_date: e.target.value }))} />
              <input type="time" className="form-input" value={editDraft.exact_time} onChange={e => setEditDraft(d => ({ ...d, exact_time: e.target.value }))} />
            </div>
            {editDraft.groups.map((g, gi) => (
              <div key={gi} style={{ marginBottom: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, flex: 1 }}>{g.service_label}</div>
                  <button className="btn btn-secondary btn-sm" onClick={() => setEditDraft(d => ({ ...d, groups: d.groups.filter((_, xi) => xi !== gi) }))}>Remove service</button>
                </div>
                {g.horses.map((h, hi) => (
                  <div key={hi} style={{ display: 'flex', alignItems: 'center', gap: 8, paddingLeft: 8, marginTop: 2 }}>
                    <span style={{ fontSize: 13, minWidth: 70 }}>{h.horse_name}</span>
                    <input className="form-input" style={{ flex: 1, fontSize: 12 }} placeholder="Notes for this horse (optional)" value={h.notes || ''} onChange={e => setEditDraft(d => { const gs = d.groups.map((x, xi) => xi === gi ? { ...x, horses: x.horses.map((y, yi) => yi === hi ? { ...y, notes: e.target.value } : y) } : x); return { ...d, groups: gs }; })} />
                    <button className="btn btn-secondary btn-sm" onClick={() => setEditDraft(d => { const gs = d.groups.map((x, xi) => xi === gi ? { ...x, horses: x.horses.filter((_, yi) => yi !== hi) } : x); return { ...d, groups: gs }; })}>Remove</button>
                  </div>
                ))}
                <select className="form-input" style={{ marginTop: 4 }} value="" onChange={e => { const h = horses.find(x => x.id === e.target.value); if (h) setEditDraft(d => { const gs = d.groups.map((x, xi) => xi === gi ? { ...x, horses: [...x.horses, { horse_id: h.id, horse_name: h.name }] } : x); return { ...d, groups: gs }; }); }}>
                  <option value="">+ Add a horse to this service...</option>
                  {horses.filter(h => !g.horses.some(x => x.horse_id === h.id)).map(h => <option key={h.id} value={h.id}>{h.name}</option>)}
                </select>
              </div>
            ))}
            <select className="form-input" style={{ marginBottom: 8 }} value="" onChange={e => { if (e.target.value) setEditDraft(d => ({ ...d, groups: [...d.groups, { service_type: e.target.value, service_label: SERVICE_MAP[e.target.value] || e.target.value, horses: [] }] })); }}>
              <option value="">+ Add another service...</option>
              {SERVICES.map(([cat, opts]) => (
                <optgroup key={cat} label={cat}>
                  {opts.filter(([v]) => !editDraft.groups.some(g => g.service_type === v)).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </optgroup>
              ))}
            </select>
            <button className="btn btn-primary btn-sm" disabled={actionId === a.id} onClick={() => saveAmend(a)}>Save & resend for approval</button>
            <button className="btn btn-secondary btn-sm" style={{ marginLeft: 6 }} onClick={() => setEditApptId(null)}>Cancel</button>
          </div>
        )}
        <button className="btn btn-secondary btn-sm" onClick={() => setDetailsId(detailsId === a.id ? null : a.id)}>🔎 Details</button>
        {detailsId === a.id && (
          <div style={{ width: '100%', marginTop: 8, padding: 10, border: '1px solid var(--border-color)', borderRadius: 10, fontSize: 13 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: 4 }}>Original appointment</div>
            <div><b>{a.farrier_name}</b>{a.farrier_phone ? ' · ' + a.farrier_phone : ''}</div>
            <div>{a.proposed_date}{a.exact_time ? ' at ' + a.exact_time : ''}{a.duration_minutes ? ' · ~' + Math.round(a.duration_minutes / 60 * 10) / 10 + ' hrs' : ''}</div>
            {a.location && <div style={{ marginTop: 4 }}><b>Location:</b> {a.location}</div>}
            {a.facility_notes && <div><b>Facility:</b> {a.facility_notes}</div>}
            {(a.service_groups || []).map(g => (
              <div key={g.id} style={{ marginTop: 6 }}>
                <b>{g.service_label}</b>
                {(g.horses || []).map(h => (
                  <div key={h.id} style={{ paddingLeft: 10 }}>• {h.horse_name_snapshot}{h.horse_notes ? ' — ' + h.horse_notes : ''}</div>
                ))}
              </div>
            ))}
            {a.message && <div style={{ marginTop: 6 }}><b>Message sent:</b> {a.message}</div>}
            {a.owner_notification_phone && <div style={{ marginTop: 4, fontSize: 12, color: 'var(--text-muted)' }}>Replies go to {a.owner_notification_phone}</div>}
            {Array.isArray(a.amendment_history) && a.amendment_history.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Change history</div>
                {a.amendment_history.map((h, i) => (
                  <div key={i} style={{ fontSize: 12, marginTop: 3 }}>
                    <b>{new Date(h.at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</b> — {(h.changes || []).join('; ')}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        <button className="btn btn-secondary btn-sm" onClick={() => { setNotesId(notesId === a.id ? null : a.id); setNotesDraft(a.owner_notes || ''); }}>📝 Notes</button>
        {(() => { const ct = contacts.find(c => c.id === a.contact_id); return (ct && (ct.provider_instructions || ct.cancellation_notice_hours)) ? <div style={{ width: '100%', fontSize: 12, color: 'var(--text-secondary)', marginTop: 6 }}>{ct.provider_instructions ? 'Provider asks: ' + ct.provider_instructions : ''}{ct.cancellation_notice_hours ? (ct.provider_instructions ? ' · ' : '') + ct.cancellation_notice_hours + 'h cancellation notice' : ''}</div> : null; })()}
        {notesId === a.id && (
          <div style={{ width: '100%', marginTop: 8, padding: 10, border: '1px solid var(--border-color)', borderRadius: 10 }}>
            {a.farrier_general_notes && (<div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Farrier's notes</div>
              <div style={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>{a.farrier_general_notes}</div>
            </div>)}
            {a.last_farrier_reply_body && (<div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Last text reply</div>
              <div style={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>{a.last_farrier_reply_body}</div>
            </div>)}
            {(() => { const hs = (a.service_groups || []).flatMap(g => g.horses || []).filter(h => h.farrier_notes); return hs.length ? (
              <div style={{ marginBottom: 8 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Notes by horse</div>
                {hs.map(h => (
                  <div key={h.id} style={{ fontSize: 13, marginTop: 2 }}>
                    <b>{h.horse_name_snapshot}:</b> {h.farrier_notes}
                  </div>
                ))}
              </div>
            ) : null; })()}
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: 4 }}>My notes</div>
            <textarea className="form-input" rows={3} value={notesDraft} onChange={e => setNotesDraft(e.target.value)} placeholder="Anything worth remembering about this visit" />
            <button className="btn btn-primary btn-sm" style={{ marginTop: 6 }} onClick={() => saveOwnerNotes(a)}>Save notes</button>
          </div>
        )}
        {['sent', 'alternate_time_suggested', 'accepted'].includes(a.status) && <button className="btn btn-secondary btn-sm" onClick={() => openReschedule(a)} disabled={actionId === a.id}>Request to reschedule</button>}
        {['declined', 'canceled'].includes(a.status) && <button className="btn btn-danger btn-sm" onClick={() => deleteAppointment(a)} disabled={actionId === a.id}>Delete request history</button>}
      </div>
    </div>
  );

  return (
    <div>
      <div className="top-bar">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button onClick={() => navigate('/horses')} style={{ background: 'none', border: 'none', color: 'white', fontSize: 20, cursor: 'pointer' }}>‹</button>
          <div>
            <h1 style={{ fontSize: 18 }}>Farrier scheduling</h1>
            <p style={{ opacity: 0.85, fontSize: 12, marginTop: 2 }}>Pick an exact time, text the farrier, and handle replies</p>
          </div>
        </div>
      </div>

      <div className="page-content">
        {loading ? <div className="loading-screen"><div className="spinner" /></div> : (
          <>
            <div className="section-label">Farrier</div>
            <div className="card">
              {farrierContacts.length > 0 && (
                <div className="form-group">
                  <label className="form-label">Saved farrier contact</label>
                  <select className="form-input" value={form.contact_id} onChange={e => pickContact(e.target.value)}>
                    <option value="">Choose saved farrier or enter manually</option>
                    {farrierContacts.map(c => <option key={c.id} value={c.id}>{c.name}{c.phone ? ` — ${c.phone}` : ''}</option>)}
                  </select>
                </div>
              )}
              <div className="form-group">
                <label className="form-label">Farrier name *</label>
                <input className="form-input" value={form.farrier_name} onChange={e => setField('farrier_name', e.target.value)} placeholder="Name" />
              </div>
              <div className="form-group">
                <label className="form-label">Farrier phone</label>
                <input className="form-input" value={form.farrier_phone} onChange={e => setField('farrier_phone', e.target.value)} placeholder="8305551234" />
              </div>
            </div>

            <div className="section-label">Requested work</div>
            <div className="card">
              <div className="form-group">
                <label className="form-label">Service</label>
                <select className="form-input" value={groupForm.service_type} onChange={e => setGroupForm(p => ({ ...p, service_type: e.target.value }))}>
                  {SERVICES.map(([label, items]) => (
                    <optgroup key={label} label={label}>
                      {items.map(([val, text]) => <option key={val} value={val}>{text}</option>)}
                    </optgroup>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Add horses for this service</label>
                <div className="chip-group">
                  {horses.map(h => <div key={h.id} className={`chip ${groupForm.horseIds.includes(h.id) ? 'selected' : ''}`} onClick={() => toggleHorse(h.id)}>{h.name}</div>)}
                </div>
              </div>
              {groupForm.horseIds.length > 0 && (
                <div className="form-group">
                  <label className="form-label">Horse-level notes for this service</label>
                  {groupForm.horseIds.map(id => {
                    const horse = horses.find(h => h.id === id);
                    return (
                      <div key={id} style={{ marginBottom: 8 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>{horse?.name || 'Horse'}</div>
                        <input className="form-input" value={groupForm.horseNotes[id] || ''} onChange={e => setGroupForm(p => ({ ...p, horseNotes: { ...p.horseNotes, [id]: e.target.value } }))} placeholder="Optional horse note" />
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="form-group">
                <label className="form-label">Notes for this service group</label>
                <input className="form-input" value={groupForm.notes} onChange={e => setGroupForm(p => ({ ...p, notes: e.target.value }))} placeholder="Optional" />
              </div>
              <button className="btn btn-secondary btn-full" onClick={saveServiceGroup}>Save service group</button>
            </div>

            {serviceGroups.length > 0 && (
              <>
                <div className="section-label">Service summary</div>
                {serviceGroups.map(g => (
                  <div key={g.id} className="card" style={{ padding: '12px 14px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 14 }}>{g.service_label} — {g.horses.length} horse{g.horses.length === 1 ? '' : 's'}</div>
                        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>{g.horses.map(h => h.name).join(', ')}</div>
                        {g.notes && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>{g.notes}</div>}
                      </div>
                      <button className="btn btn-danger btn-sm" onClick={() => removeGroup(g.id)}>Remove</button>
                    </div>
                  </div>
                ))}
              </>
            )}

            <div className="section-label">Proposed appointment</div>
            <div className="card">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div className="form-group">
                  <label className="form-label">Date *</label>
                  <select className="form-input" style={{ marginBottom: 8 }} value="" onChange={e => { if (e.target.value) { setField('proposed_date', e.target.value); } }}><option value="">Quick pick a date…</option>{(() => { const opts = []; const push = (base, label) => [4, 6, 8].forEach(w => { const d = new Date(base); d.setDate(d.getDate() + w * 7); opts.push({ iso: d.toISOString().slice(0, 10), text: '+' + w + ' wks from ' + label + ' — ' + d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) }); }); push(new Date(), 'today'); const last = (appointments || []).map(a => a.proposed_date).filter(Boolean).sort().slice(-1)[0]; if (last) push(new Date(last + 'T00:00:00'), 'last appt (' + new Date(last + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ')'); return opts.map((o, k) => <option key={k} value={o.iso}>{o.text}</option>); })()}</select><input className="form-input" type="date" value={form.proposed_date} onChange={e => setField('proposed_date', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Exact time *</label>
                  <input className="form-input" type="time" value={form.exact_time} onChange={e => setField('exact_time', e.target.value)} />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div className="form-group">
                  <label className="form-label">Estimated duration</label>
                  <select className="form-input" value={form.duration_minutes} onChange={e => setField('duration_minutes', e.target.value)}>
                    <option value={30}>30 minutes</option>
                    <option value={60}>1 hour</option>
                    <option value={90}>1.5 hours</option>
                    <option value={120}>2 hours</option>
                    <option value={180}>3 hours</option>
                    <option value={240}>4 hours</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Time zone</label>
                  <input className="form-input" value={form.time_zone} onChange={e => setField('time_zone', e.target.value)} placeholder="America/Chicago" />
                </div>
              </div>
              <div className="form-group"><label className="form-label">Who will meet the farrier (optional)</label><input className="form-input" placeholder="Leave blank if it's you" value={form.meet_person || ''} onChange={e => setField('meet_person', e.target.value)} /></div>
            <div className="form-group">
                <label className="form-label">Send farrier responses to *</label>
                <input className="form-input" value={form.owner_notification_phone} onChange={e => setField('owner_notification_phone', e.target.value)} placeholder="Who should receive accept/decline/reschedule texts?" />
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>This can be the barn owner, trainer, staff member, or horse owner handling this appointment.</div>
              </div>
              <div className="form-group">
                <label className="form-label">Location</label>
                <input className="form-input" value={form.location} onChange={e => setField('location', e.target.value)} placeholder="Barn address or directions" />
              </div>
              <div className="form-group">
                <label className="form-label">Facility notes</label>
                <textarea className="form-input" rows="3" value={form.facility_notes} onChange={e => setField('facility_notes', e.target.value)} placeholder="Gate code, parking, dogs, where horses are located" />
              </div>
              <div className="form-group">
                <label className="form-label">Message to farrier</label>
                <textarea className="form-input" rows="3" value={form.message} onChange={e => setField('message', e.target.value)} placeholder="Optional message" />
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 12 }}>
                Current request: {serviceGroups.length} service group{serviceGroups.length === 1 ? '' : 's'} · {appointmentCount} horse service{appointmentCount === 1 ? '' : 's'}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
                Text replies supported: YES accepts, NO declines, ALT 7/15 10:30AM saves an alternate exact time for you to approve.
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <button className="btn btn-secondary" onClick={saveDraft} disabled={saving}>{saving ? 'Saving...' : 'Save draft'}</button>
                <button className="btn btn-primary" onClick={textFarrier} disabled={saving || !!sendingId}>{sendingId ? 'Sending...' : 'Text farrier'}</button>
              </div>
            </div>

            <div className="section-label">Active farrier requests</div>
            {activeAppointments.length === 0 ? (
              <div className="empty-state"><div className="empty-state-icon">🔨</div><h3>No active farrier requests</h3><p>Create a request above, then text it to your farrier.</p></div>
            ) : activeAppointments.map(renderAppointmentCard)}

            <div className="section-label">Recent farrier history</div>
            {historyAppointments.length === 0 ? (
              <div className="card" style={{ fontSize: 13, color: 'var(--text-secondary)' }}>No closed farrier requests yet.</div>
            ) : (
              <>
                <div className="section-label" style={{ marginTop: 18 }}>📘 Service history ({historyAppointments.length})</div>
                {historyAppointments.length === 0 && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>Completed and canceled appointments are kept here permanently — services, horses, and notes included.</div>}
                {visibleHistory.map(renderAppointmentCard)}
                {historyAppointments.length > 5 && (
                  <button className="btn btn-secondary btn-full" onClick={() => setShowAllHistory(v => !v)}>{showAllHistory ? 'Show last 5 only' : `View all saved history (${historyAppointments.length})`}</button>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default FarrierPage;
